import {computed, inject, Injectable, signal} from '@angular/core';
import {ApiService, IImgUrls} from './api.service';
import {catchError, EMPTY, tap} from 'rxjs';
import {HttpClient} from '@angular/common/http';
import {Router} from '@angular/router';
import {AUTH_KEYS} from './auth-constants';
import {LoadingService} from './loading.service';
import {IDictionary} from './dictionary.service';
import {CooperationFormat} from '../enums/cooperation-format.enum';
import {AuthService} from './auth.service';
import {
  BriefsSellerIntegrationsUpdateComponent
} from '../../base/briefs/briefs-seller/briefs-seller-integrations-update/briefs-seller-integrations-update.component';

export interface IBriefs {
  id: number;
  name: string;
  userId: number;
  briefStatus: string;
  description: string;
  briefReferences: string;
  offerType: string;
  offerName: string;
  offerDescription: string;
  integrationAmount: number;
  categories: IDictionary[];
  workFormats: IDictionary[];
  createdAt: string;
  updatedAt: string;
  imageUrl: IImgUrls;
  imageUrls: IImgUrls;
  denyReason: string;
  integrationPrice: number;
  wasReturned: boolean;
  paidIntegrations: number;
  occupiedIntegrations: number;
  finishedIntegrations: number;
  minDaysForAdvert: number;
  platforms: string[];
  disputeChatsCount: number;
  disputeChatsActiveExists: boolean;
  unreadMessagesCount: number;
  unreadChatMessagesCount: number;
  //seller
  newIntegrations: number;
  verifyingReportsAmount: number;
  wasRecalled: string;
  status: string;
  werePaid?: number;
  integrationStatus: string;
  integrationId: number;
  allReportsDenied: boolean;
  chatId?: number;
  niche:string;
  cooperationFormat: CooperationFormat;
  commissionPerIntegration?: number;
  extendedInfo?: Record<string, any>;
  isFinishedBySeller?:boolean;

}

export interface IBriefsGeo {
  id: number;
  name: string;
  description: string;
  offerType: string;
  imageUrls: IImgUrls;
  integrationPrice: number;
  platforms: string[];
  workFormats: IDictionary[];
  cooperationFormat: CooperationFormat;
  niche: string;
  geoLocation: {
    longitude: number;
    latitude: number;
  };
}

export interface IBriefsGeoQuery {
  limit: number;
  offerType: string;
  platforms: string[];
  priceFrom: number | null;
  priceTo: number | null;
  workFormatIds: number[];
  categoryIds: number[];
  search: string;
  niches: string[];
  cooperationFormats: string[];
  sessionId: string;
  minLongitude: number;
  minLatitude: number;
  maxLongitude: number;
  maxLatitude: number;
}

export interface IBriefsOpponent {
  id: number;
  name: string;
  integrationStatus: string;
  unreadInChatAmt: number;
  createdAt: string;
  updatedAt: string;
  chatId: number;
  image: IImgUrls;
}

export interface IBriefsPay {
  werePaid: number;
  totalIntegrationPrice: number;
  integrationPriceDiff: number;
  rent: number;
  totalPrice: number;
}


@Injectable({
  providedIn: 'root'
})
export class BriefsApiService {
  loadingService = inject(LoadingService);
  apiService = inject(ApiService);
  authService = inject(AuthService);

  User = this.authService.user

  constructor() {
  }

  CreateBriefs(data: any) {
    return this.apiService.SendPost(`/user/brief`, data, this.loadingService.StatesNames.BRIEFS_CREATE_EDIT);
  }

  EditBriefs(id: number, data: any) {
    return this.apiService.SendPatch(`/user/brief/${id}`, data, this.loadingService.StatesNames.BRIEFS_CREATE_EDIT);
  }

  StatusChangeBriefs(id: number, data: any) {
    return this.apiService.SendPatch(`/user/brief/${id}/status-change`, data, this.loadingService.StatesNames.BRIEFS_STATUS_CHANGE);
  }

  UpdateIntegrationsBriefs(id: number, data: any) {
    return this.apiService.SendPatch(`/user/paused-brief/${id}`, data, this.loadingService.StatesNames.BRIEFS_UPDATE_INTEGRATIONS);
  }

  RecallBriefs(id: number) {
    return this.apiService.SendPatch(`/seller/brief/${id}/recall`, this.loadingService.StatesNames.BRIEFS_RECALL);
  }

  DeleteBriefs(id: number) {
    return this.apiService.SendDelete(`/user/brief/${id}`, this.loadingService.StatesNames.BRIEFS_DELETE);
  }

  GetBriefsListBlogger(
    limit: number,
    page: number,
    hasFreeIntegrations: boolean | null,
    offerType: string,
    platforms: string[],
    priceFrom: number,
    priceTo: number,
    workFormatIds: number[],
    categoryIds: number[],
    search: string,
    niches: string[] = [],
    cooperationFormats: string[] = []
  ) {
    const params: Record<string, string> = {
      limit: limit.toString(),
      page: page.toString(),
    };

    if (hasFreeIntegrations !== null) {
      params['hasFreeIntegrations'] = hasFreeIntegrations.toString();
    }
    if (offerType) {
      params['offerType'] = offerType;
    }
    if (platforms?.length) {
      params['platforms'] = platforms.join(',');
    }
    if ((priceTo !== undefined && priceTo !== 0) && (priceFrom !== undefined)) {
      params['priceFrom'] = priceFrom!.toString();
      params['priceTo'] = priceTo!.toString();
    }
    if (workFormatIds?.length) {
      params['workFormatIds'] = workFormatIds.join(',');
    }
    if (categoryIds?.length) {
      params['categoryIds'] = categoryIds.join(',');
    }
    if (search) {
      params['search'] = search;
    }
    if (niches?.length) {
      params['niches'] = niches.join(',');
    }
    if (cooperationFormats?.length) {
      params['cooperationFormats'] = cooperationFormats.join(',');
    }

    const queryString = '?' + new URLSearchParams(params).toString();
    return this.apiService.SendGet(`/blogger/briefs${queryString}`, this.loadingService.StatesNames.BRIEFS_GET);
  }

  GetBriefsGeo(query: IBriefsGeoQuery) {
    const params: Record<string, string> = {
      limit: query.limit.toString(),
      sessionId: query.sessionId,
      minLongitude: query.minLongitude.toString(),
      minLatitude: query.minLatitude.toString(),
      maxLongitude: query.maxLongitude.toString(),
      maxLatitude: query.maxLatitude.toString(),
    };

    if (query.offerType) {
      params['offerType'] = query.offerType;
    }
    if (query.platforms.length) {
      params['platforms'] = query.platforms.join(',');
    }
    if (query.priceFrom !== null) {
      params['priceFrom'] = query.priceFrom.toString();
    }
    if (query.priceTo !== null) {
      params['priceTo'] = query.priceTo.toString();
    }
    if (query.workFormatIds.length) {
      params['workFormatIds'] = query.workFormatIds.join(',');
    }
    if (query.categoryIds.length) {
      params['categoryIds'] = query.categoryIds.join(',');
    }
    if (query.search) {
      params['search'] = query.search;
    }
    if (query.niches.length) {
      params['niches'] = query.niches.join(',');
    }
    if (query.cooperationFormats.length) {
      params['cooperationFormats'] = query.cooperationFormats.join(',');
    }

    const queryString = new URLSearchParams(params).toString();
    return this.apiService.SendGet(`/blogger/briefs-geo?${queryString}`, this.loadingService.StatesNames.BRIEFS_GEO_GET);
  }

  GetBriefsListSeller(
    limit: number,
    page: number,
    briefStatuses: string[],
    from: string,
    to: string,
    filter: string,
    search: string,
    categoryIds?: number[],
    niches?: string[],
    cooperationFormats?: string[]
  ) {
    const params: Record<string, string> = {
      limit: limit.toString(),
      page: page.toString(),
    };

    if (briefStatuses?.length) {
      params['briefStatuses'] = briefStatuses.join(',');
    }
    if (from && to) {
      params['from'] = from;
      params['to'] = to;
    }
    if (filter) {
      params['filter'] = filter;
    }
    if (search) {
      params['search'] = search;
    }
    if (categoryIds?.length) {
      params['categoryIds'] = categoryIds.join(',');
    }
    if (niches?.length) {
      params['niches'] = niches.join(',');
    }
    if (cooperationFormats?.length) {
      params['cooperationFormats'] = cooperationFormats.join(',');
    }

    const queryString = '?' + new URLSearchParams(params).toString();
    return this.apiService.SendGet(`/seller/briefs${queryString}`, this.loadingService.StatesNames.BRIEFS_GET);
  }

  GetBriefsItem(id: number, withLoading = true) {
    return this.apiService.SendGet(`/${this.User()?.role?.toLowerCase()}/brief/${id}`, withLoading ? this.loadingService.StatesNames.BRIEFS_GET_ITEM : this.loadingService.StatesNames.UNKNOWN);
  }

  GetBriefsPay(id: number, newPrice: number, newAmount: number) {
    let queryParams: string[] = [];

    if (newPrice > 0) {
      queryParams.push(`newPrice=${newPrice}`);
    }

    if (newAmount > 0) {
      queryParams.push(`newAmount=${newAmount}`);
    }

    const queryString = queryParams.length ? `?${queryParams.join('&')}` : '';
    return this.apiService.SendGet(`/seller/brief/${id}/pay-calc${queryString}`, this.loadingService.StatesNames.BRIEFS_GET_PAY);
  }

  PostBriefsPay(id: number) {
    return this.apiService.SendPost(`/seller/brief/${id}/pay`, this.loadingService.StatesNames.BRIEFS_POST_PAY);
  }

  GetBriefsByOpponent(id: number) {
    return this.apiService.SendGet(`/${this.User()?.role?.toLowerCase()}/briefs/by-${this.User()?.role === 'SELLER' ? 'blogger' : 'seller'}/${id}`, this.loadingService.StatesNames.BRIEFS_GET_LIST_OPPONENT);
  }
}

@Injectable({
  providedIn: 'root'
})
export class BriefsService {
  briefsApiService = inject(BriefsApiService);
  authService = inject(AuthService);

  BriefsListBlogger = signal<IBriefs[]>([]);
  BriefsListSeller = signal<IBriefs[]>([]);
  BriefsListOpponent = signal<IBriefsOpponent[]>([]);
  Brief = signal<IBriefs>({} as IBriefs);
  BriefPay = signal<IBriefsPay>({} as IBriefsPay);

  totalPages = signal<number>(0);
  totalElements = signal<number>(0);

  constructor() {
  }

  getBriefsListBlogger(
    {limit, page, hasFreeIntegrations, offerType, platforms, priceFrom, priceTo, workFormatIds, categoryIds, search, niches, cooperationFormats}:
      {
        limit: number;
        page: number;
        hasFreeIntegrations: boolean | null;
        offerType: string;
        platforms: string[];
        priceFrom: number;
        priceTo: number;
        workFormatIds: number[];
        categoryIds: number[];
        search: string;
        niches: string[];
        cooperationFormats: string[];
      }
  ) {
    return this.briefsApiService.GetBriefsListBlogger(limit, page, hasFreeIntegrations, offerType, platforms, priceFrom, priceTo, workFormatIds, categoryIds, search, niches, cooperationFormats).pipe(
      tap((res) => {
        this.BriefsListBlogger.set(res['content']);
        this.totalPages.set(res['page']['totalPages']);
        this.totalElements.set(res['page']['totalElements']);
      })
    )
  }

  getBriefsGeo(query: IBriefsGeoQuery) {
    return this.briefsApiService.GetBriefsGeo(query);
  }

  getBriefsListSeller(
    {limit, page, briefStatuses, from, to, filter, search, categoryIds, niches, cooperationFormats}:
      {
        limit: number;
        page: number;
        briefStatuses: string[];
        from: string;
        to: string;
        filter: string;
        search: string;
        categoryIds?: number[];
        niches?: string[];
        cooperationFormats?: string[];
      }
  ) {
    return this.briefsApiService.GetBriefsListSeller(limit, page, briefStatuses, from, to, filter, search, categoryIds, niches, cooperationFormats).pipe(
      tap((res) => {
        this.BriefsListSeller.set(res['content']);
        this.totalPages.set(res['page']['totalPages']);
        this.totalElements.set(res['page']['totalElements']);
      })
    )
  }

  getBriefItem(id: number, withLoading = true) {
    return this.briefsApiService.GetBriefsItem(id, withLoading).pipe(
      tap((res) => {
        this.Brief.set(res)
        // if(withLoading) {
        //   this.Brief.set({...res, chatId: null })
        // } else {
        // }
        return res;
      }),
    );
  }

  getBrief(id: number) {
    return this.briefsApiService.GetBriefsItem(id);
  }

  getBriefsPay(
    {id, newPrice, newAmount}:
      {
        id: number;
        newPrice: number;
        newAmount: number;
      }
  ) {
    return this.briefsApiService.GetBriefsPay(id, newPrice, newAmount).pipe(
      tap((res) => {
        this.BriefPay.set(res)
        return res;
      }),
    );
  }

  postBriefsPay(id: number, sum: number) {
    return this.briefsApiService.PostBriefsPay(id).pipe(
      tap((res) => {
        this.authService.updateUser({actualBalance: sum});
        this.updateBrief(res);
        return res;
      }),
    );
  }

  getBriefsByOpponent(id: number) {
    return this.briefsApiService.GetBriefsByOpponent(id).pipe(
      tap((res) => {
        this.BriefsListOpponent.set(res);
      }),
    );
  }

  createBriefs(data: any) {
    return this.briefsApiService.CreateBriefs(data).pipe(
      tap((res) => {
        this.updateBrief(res);
        this.Brief.set(res);
      })
    )
  }

  editBriefs(id: number, data: any) {
    return this.briefsApiService.EditBriefs(id, data).pipe(
      tap((res) => {
        this.updateBrief(res);
        this.BriefsListSeller.set(
          this.BriefsListSeller().map(brief => (brief.id === res.id ? res : brief))
        );
      }),
    )
  }

  statusChangeBriefs(id: number, data: any) {
    return this.briefsApiService.StatusChangeBriefs(id, data).pipe(
      tap((res) => {
        this.updateBrief({briefStatus: res['briefStatus']});
        this.BriefsListSeller.set(
          this.BriefsListSeller().map(brief => brief.id === id ? {...brief, status: res['briefStatus']} : brief)
        );
      }),
    )
  }

  updateIntegrationsBriefs(id: number, data: any, sum: number) {
    return this.briefsApiService.UpdateIntegrationsBriefs(id, data).pipe(
      tap((res) => {
        this.authService.updateUser({actualBalance: sum});
        this.updateBrief({integrationAmount: res['integrationAmount'], integrationPrice: res['integrationPrice']});
      }),
    )
  }

  recallBriefs(id: number) {
    return this.briefsApiService.RecallBriefs(id).pipe(
      tap((res) => {
        this.updateBrief({briefStatus: 'DRAFT'});
        this.BriefsListSeller.set(
          this.BriefsListSeller().map(brief => brief.id === id ? {...brief, status: 'DRAFT'} : brief)
        );
      }),
    )
  }

  deleteBriefs(id: number) {
    return this.briefsApiService.DeleteBriefs(id).pipe(
      tap((res) => {
        this.BriefsListSeller.set(this.BriefsListSeller().filter(brief => brief.id !== id));
      })
    )
  }

  clearBrief(): void {
    this.Brief.set({} as IBriefs);
  }

  updateBrief(data: any): void {
    this.Brief.set({
      ...this.Brief(),
      ...data
    });
  }
}
